import * as _ from "lodash-es";
import {
  RELATIONSHIPS,
  NOT_AT_SCHOOL_OPTION,
  RESIDES_CHOICES,
  RESIDES_WITH_OTHER,
  OTHER_SCHOOL_OPTION,
  OTHER_LANGUAGE,
  FEMALE_CONNECTION_TYPES,
  MALE,
  FEMALE,
  OTHER_GENDER,
  ALL_CONNECTION_TYPE_CHOICES,
  OTHER_SOCICAL_UNIT_OPTION,
} from "@/constants";
import initialData from "@/initialData";
import { isOther, getOptionsFieldDisplay } from "@/form-utils";
import { isEnabled } from "@/features/metafields/services";

/**
 * @param {Object} parent Parent
 * @returns {String}
 */
export const getRelationshipToStudentDisplay = (parent) => {
  if (_.isNil(parent.relationship_to_student)) {
    // May be 0
    return parent.relationship_to_student;
  }
  return _.keyBy(RELATIONSHIPS, "value")[parent.relationship_to_student].text;
};

/**
 * @param {Object} profile Student profile.
 * @returns {String}
 */
export const getCurrentSchoolDisplay = (profile) => {
  if (!profile.current_school) {
    return profile.current_school;
  }
  if (profile.current_school.id === OTHER_SCHOOL_OPTION.id) {
    return profile.current_school_other;
  }
  if (profile.current_school.id === NOT_AT_SCHOOL_OPTION.id) {
    return NOT_AT_SCHOOL_OPTION.name;
  }
  return `${profile.current_school.name} - ${profile.current_school.suburb}, ${profile.current_school.state}`;
};

/**
 * @param {Object} person Student profile or parent.
 * @returns {String}
 */
export const getHomeLanguageDisplay = (person) => {
  if (!person.home_language) {
    return person.home_language;
  }
  if (person.home_language.id === OTHER_LANGUAGE.id) {
    return person.home_language_other;
  }
  return person.home_language.name;
};

/**
 * @param {Object} person Student profile or parent.
 * @returns {String}
 */
export const getReligionDisplay = (person) => {
  return getOptionsFieldDisplay(person, "religion");
};

export const getCorrespondenceAddresseeDisplay = (profile) => {
  return getMultipleChoiceDisplay([
    ...(profile.correspondence_addressee || []),
    profile.correspondence_addressee_other,
  ]);
};

// Keep in sync with `student_resides_with_str`
export const getStudentResidesWithDisplay = (profile) => {
  if (_.isNil(profile.student_resides_with)) {
    // May be 0
    return profile.student_resides_with;
  }
  if (
    profile.student_resides_with === RESIDES_WITH_OTHER &&
    profile.student_resides_with_other
  ) {
    return profile.student_resides_with_other;
  }
  return _.keyBy(RESIDES_CHOICES, "value")[profile.student_resides_with].text;
};

export const changeNumberOfAttendees = (attendees, number) => {
  const diff = number - attendees.length;
  if (diff <= 0) {
    return attendees.slice(0, number);
  }
  return attendees.concat(_.range(diff).map(initialData.getAttendee));
};

export const getGenderDisplay = (gender) => {
  // Keep in sync with django gender filter
  if (gender === MALE) {
    return "son";
  }
  if (gender === FEMALE) {
    return "daughter";
  }
  return "child";
};

export const getGenderPronoun = (gender) => {
  if (gender === MALE) {
    return "he";
  }
  if (gender === FEMALE) {
    return "she";
  }
  return "he/she";
};

export const getNotOtherOptions = (options) =>
  options.filter((opt) => opt && !isOther(opt));

// Keep in sync with `is_personal_referral_option`.
export const isPersonalReferralOption = (option) =>
  option.toLowerCase().trim() === "personal referral";

// Keep in sync with `get_option_values`.
export const getMultipleChoiceDisplay = (options) =>
  getNotOtherOptions(options).join(", ");

// Keep in sync with `get_how_hear_option_values`.
export const getHowHearDisplay = (obj) =>
  getNotOtherOptions([
    ...(obj.how_hear || []),
    obj.how_hear_other,
    obj.how_hear_personal_referral,
  ])
    .filter((option) => !isPersonalReferralOption(option))
    .join(", ");

export const getMedicalConditionsDisplay = (medicalData) => {
  return (
    medicalData &&
    getMultipleChoiceDisplay([
      ...(medicalData.medical_conditions || []),
      medicalData.medical_conditions_other,
    ])
  );
};

/**
 * Return true if maiden name allow for this alumnus conection type
 *
 * @param {String} alumnusConnectionType alumnus conection type
 * @returns {Boolean}
 */
export const hasMaidenName = (alumnusConnectionType) => {
  return FEMALE_CONNECTION_TYPES.includes(alumnusConnectionType);
};

export const getConnectionTypeDisplay = (connectionType, other) => {
  if (isOther(connectionType)) {
    return other;
  }
  return (
    connectionType && _.keyBy(ALL_CONNECTION_TYPE_CHOICES, "value")[connectionType].text
  );
};

/**
 * Init profile with empty fields that are not received from backend.
 * Some fields are write-only, some are fake (used just to show errors).
 * This is required because `traverseErrors` doesn't collect errors if
 * corresponding fields are not in data.
 *
 * @param {Object} profile Student profile
 */
export const initEmptyFields = (profile) => ({
  ...profile,
  // Add signature fields to show errors
  user_parent_signature: null,
  non_user_parent_signature: null,
  // Add avatar to show errors.
  // `null` can clear file on backend.
  avatar: undefined,
  // Add identification image to show errors
  user_parent: profile.user_parent && {
    identification_image: undefined,
    ...profile.user_parent,
  },
  non_user_parent: profile.non_user_parent && {
    identification_image: undefined,
    ...profile.non_user_parent,
  },
});

export const isCurrentSchoolBlockEnabled = (formCode) => {
  const block = window.SchoolSettings.metafields.profile;
  return [
    "current_school",
    "current_school_grade",
    "current_school_commence_date",
    "current_school_history",
    "nesa_number",
    "vsn",
  ].some((fieldName) => {
    return isEnabled(block[fieldName].enabled, formCode);
  });
};

export const getGenderLabel = (genderValue) => {
  return {
    [MALE]: "Male",
    [FEMALE]: "Female",
    [OTHER_GENDER]: window.SchoolSettings.metafields.profile.gender.other_gender_label,
  }[genderValue];
};

export const getGenderOptions = () =>
  [
    { value: MALE, text: getGenderLabel(MALE) },
    { value: FEMALE, text: getGenderLabel(FEMALE) },
    window.SchoolSettings.metafields.profile.gender.is_other_gender_enabled && {
      value: OTHER_GENDER,
      text: getGenderLabel(OTHER_GENDER),
    },
  ].filter((opt) => opt);

export const getFirstParentKey = (profile) =>
  profile.first_parent_field !== "non_user_parent" ? "user_parent" : "non_user_parent";

export const getSecondParentKey = (profile) =>
  profile.first_parent_field === "non_user_parent" ? "user_parent" : "non_user_parent";

export const isFirstParentUserParent = (profile) =>
  getFirstParentKey(profile) !== "non_user_parent";

export const getNonUserParentLabel = (profile) => {
  if (isFirstParentUserParent(profile)) {
    return "Parent 2";
  }
  return "Parent 1";
};

export const getUserParentLabel = (profile) => {
  if (isFirstParentUserParent(profile)) {
    return "Parent 1";
  }
  return "Parent 2";
};

export const getFirstParent = (profile) => profile[getFirstParentKey(profile)];

export const getSecondParent = (profile) => profile[getSecondParentKey(profile)];

export const isOtherSocialUnit = (socialUnit) =>
  socialUnit.id === OTHER_SOCICAL_UNIT_OPTION.id;

const getSocialUnitDisplay = (socialUnit, socialUnitsOther) => {
  if (isOtherSocialUnit(socialUnit)) {
    return socialUnitsOther;
  }
  return socialUnit.name;
};

export const getSocialUnitsDisplay = (socialUnits, socialUnitsOther) => {
  return (socialUnits || [])
    .map((u) => getSocialUnitDisplay(u, socialUnitsOther))
    .filter((u) => u)
    .join(", ");
};

export const getNationalitiesDisplay = (nationalities) => {
  return (nationalities || []).map((n) => n.name).join(", ");
};
